import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  Building2, Users, Crown, ShieldCheck, User, Mail, Plus, Loader2, ArrowLeft,
  MoreVertical, Trash2, UserMinus, RefreshCw, Copy, Check, X, Briefcase
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const roleBadge = (role) => {
  const map = {
    owner: { label: 'Owner', icon: <Crown className="w-3 h-3" />, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    admin: { label: 'Admin', icon: <ShieldCheck className="w-3 h-3" />, cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    member: { label: 'Member', icon: <User className="w-3 h-3" />, cls: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
  };
  const r = map[role] || map.member;
  return (
    <Badge variant="outline" className={`flex items-center gap-1 ${r.cls}`}>
      {r.icon}
      <span className="text-[10px] capitalize">{r.label}</span>
    </Badge>
  );
};

export const OrganizationDetailsPage = () => {
  const { id } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteClientId, setInviteClientId] = useState('');
  const [inviteClients, setInviteClients] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [transferTo, setTransferTo] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Phase B — client-access editor state
  const [accessFor, setAccessFor] = useState(null); // member object being edited
  const [ownerClients, setOwnerClients] = useState([]);
  const [accessDraft, setAccessDraft] = useState({ mode: 'all', ids: [] });
  const [savingAccess, setSavingAccess] = useState(false);

  const isOwner = org?.my_role === 'owner';
  const isAdminOrOwner = isOwner || org?.my_role === 'admin';

  const fetchOrg = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/orgs/${id}`);
      setOrg(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load organization');
      navigate('/organizations');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    if (inviteRole === 'client' && !inviteClientId) { toast.error('Pick a client for this portal invite'); return; }
    setInviting(true);
    try {
      const body = { email: inviteEmail.trim(), role: inviteRole };
      if (inviteRole === 'client') body.client_id = inviteClientId;
      const res = await api.post(`/orgs/${id}/invites`, body);
      toast.success(res.data.email_sent ? 'Invite sent by email' : 'Invite created (email not sent)');
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      setInviteClientId('');
      fetchOrg();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  // Load the owner's client roster when switching the invite to a client invite.
  const onInviteRoleChange = async (role) => {
    setInviteRole(role);
    if (role === 'client' && inviteClients.length === 0) {
      try {
        const res = await api.get(`/orgs/${id}/owner-clients`);
        setInviteClients(res.data.clients || []);
      } catch { /* silent */ }
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await api.delete(`/orgs/${id}/invites/${inviteId}`);
      toast.success('Invite revoked');
      fetchOrg();
    } catch {
      toast.error('Failed to revoke invite');
    }
  };

  const handleChangeRole = async (memberUserId, newRole) => {
    try {
      await api.put(`/orgs/${id}/members/${memberUserId}/role`, { role: newRole });
      toast.success('Role updated');
      fetchOrg();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (memberUserId, email) => {
    if (!window.confirm(`Remove ${email} from the organization?`)) return;
    try {
      await api.delete(`/orgs/${id}/members/${memberUserId}`);
      toast.success('Member removed');
      if (memberUserId === user.id) navigate('/organizations');
      else fetchOrg();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleTransfer = async () => {
    if (!transferTo) return;
    try {
      await api.post(`/orgs/${id}/transfer-ownership`, { new_owner_user_id: transferTo.user_id });
      toast.success(`Ownership transferred to ${transferTo.email}`);
      setTransferTo(null);
      fetchOrg();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer ownership');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/orgs/${id}`);
      toast.success('Organization deleted');
      navigate('/organizations');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const copyInviteLink = (token) => {
    const url = `${window.location.origin}/invites/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Invite link copied');
  };

  // Phase B — open the client-access editor for a given member.
  // Lazily fetches the owner's client roster so we don't pay for it on
  // page-load when no one is editing.
  const openClientAccess = async (member) => {
    setAccessFor(member);
    const allowed = member.allowed_client_ids;
    setAccessDraft(
      allowed === null || allowed === undefined
        ? { mode: 'all', ids: [] }
        : { mode: 'scoped', ids: [...allowed] }
    );
    if (ownerClients.length === 0) {
      try {
        const res = await api.get(`/orgs/${id}/owner-clients`);
        setOwnerClients(res.data.clients || []);
      } catch {
        toast.error('Failed to load client roster');
      }
    }
  };

  const saveClientAccess = async () => {
    if (!accessFor) return;
    setSavingAccess(true);
    try {
      const body = {
        allowed_client_ids: accessDraft.mode === 'all' ? null : accessDraft.ids,
      };
      await api.put(
        `/orgs/${id}/members/${accessFor.user_id}/clients`,
        body,
      );
      toast.success('Client access updated');
      setAccessFor(null);
      fetchOrg();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSavingAccess(false);
    }
  };

  if (loading || !org) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="org-details-page">
      <div>
        <Link to="/organizations" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" />
          Back to organizations
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
            <Building2 className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold tracking-tight text-zinc-900" data-testid="org-name-heading">
                {org.name}
              </h1>
              {roleBadge(org.my_role)}
            </div>
            {org.description && <p className="text-zinc-500 text-sm mt-1">{org.description}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          {isAdminOrOwner && (
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={() => setInviteOpen(true)}
              data-testid="invite-member-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Invite member
            </Button>
          )}
          {isOwner && (
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => setDeleteOpen(true)}
              data-testid="delete-org-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Members */}
      <Card className="border-zinc-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-zinc-500" />
            Members ({org.members.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {org.members.map((m) => (
            <div
              key={m.user_id}
              className="flex items-center justify-between p-3 rounded-lg border border-zinc-100 hover:border-zinc-200"
              data-testid={`member-row-${m.email}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-zinc-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 truncate">{m.name || m.email}</p>
                  <p className="text-xs text-zinc-500 truncate">{m.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {/* Phase B — show a small client-scope chip on non-owner members */}
                {m.role !== 'owner' && (
                  <Badge
                    variant="outline"
                    className={
                      m.allowed_client_ids === null || m.allowed_client_ids === undefined
                        ? 'bg-zinc-50 text-zinc-600 border-zinc-200 text-[10px]'
                        : 'bg-amber-50 text-amber-700 border-amber-200 text-[10px]'
                    }
                    data-testid={`client-access-chip-${m.email}`}
                  >
                    <Briefcase className="w-3 h-3 mr-1" />
                    {m.allowed_client_ids === null || m.allowed_client_ids === undefined
                      ? 'All clients'
                      : `${m.allowed_client_ids.length} client${m.allowed_client_ids.length === 1 ? '' : 's'}`}
                  </Badge>
                )}
                {roleBadge(m.role)}
                {(isAdminOrOwner || m.user_id === user.id) && m.role !== 'owner' && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`member-menu-${m.email}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {isAdminOrOwner && m.role === 'member' && (
                        <DropdownMenuItem onClick={() => handleChangeRole(m.user_id, 'admin')}>
                          <ShieldCheck className="w-4 h-4 mr-2" />
                          Promote to admin
                        </DropdownMenuItem>
                      )}
                      {isAdminOrOwner && m.role === 'admin' && (
                        <DropdownMenuItem onClick={() => handleChangeRole(m.user_id, 'member')}>
                          <User className="w-4 h-4 mr-2" />
                          Demote to member
                        </DropdownMenuItem>
                      )}
                      {isOwner && (
                        <DropdownMenuItem onClick={() => openClientAccess(m)} data-testid={`manage-access-${m.email}`}>
                          <Briefcase className="w-4 h-4 mr-2" />
                          Manage client access
                        </DropdownMenuItem>
                      )}
                      {isOwner && (
                        <DropdownMenuItem onClick={() => setTransferTo(m)}>
                          <Crown className="w-4 h-4 mr-2" />
                          Transfer ownership
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => handleRemoveMember(m.user_id, m.email)}
                        className="text-red-600"
                      >
                        <UserMinus className="w-4 h-4 mr-2" />
                        {m.user_id === user.id ? 'Leave organization' : 'Remove from org'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Pending invites */}
      {isAdminOrOwner && (
        <Card className="border-zinc-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4 text-zinc-500" />
              Pending invites ({org.pending_invites?.length || 0})
            </CardTitle>
            <CardDescription>Invitations expire 7 days after they're sent.</CardDescription>
          </CardHeader>
          <CardContent>
            {!org.pending_invites?.length ? (
              <p className="text-sm text-zinc-500 text-center py-6">No pending invites</p>
            ) : (
              <div className="space-y-2">
                {org.pending_invites.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-zinc-100"
                    data-testid={`invite-row-${inv.email}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-900 truncate">{inv.email}</p>
                      <p className="text-xs text-zinc-500">
                        as <span className="capitalize">{inv.role}</span> · expires {new Date(inv.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => copyInviteLink(inv.token)}
                        title="Copy invite link"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 text-red-600 border-red-200"
                        onClick={() => handleRevokeInvite(inv.id)}
                        title="Revoke"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite to {org.name}</DialogTitle>
            <DialogDescription>They'll receive an email with a link to join.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="teammate@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={onInviteRoleChange}>
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="client">Client (read-only portal)</SelectItem>
                </SelectContent>
              </Select>
              {inviteRole === 'client' && (
                <p className="text-[11px] text-zinc-500">
                  They get a read-only portal to connect their accounts and approve posts — they can't create or publish.
                </p>
              )}
            </div>
            {inviteRole === 'client' && (
              <div className="space-y-2">
                <Label>Client (brand)</Label>
                <Select value={inviteClientId} onValueChange={setInviteClientId}>
                  <SelectTrigger data-testid="invite-client-select">
                    <SelectValue placeholder={inviteClients.length ? 'Select a client' : 'No clients yet — create one first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {inviteClients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={inviting}>Cancel</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
              data-testid="invite-submit-btn"
            >
              {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Send invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer ownership dialog */}
      <Dialog open={!!transferTo} onOpenChange={(v) => !v && setTransferTo(null)}>
        <DialogContent className="max-w-md" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle>Transfer ownership</DialogTitle>
            <DialogDescription>
              {transferTo
                ? <>This will make <strong>{transferTo.email}</strong> the owner. You'll be demoted to admin.</>
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferTo(null)}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleTransfer}
              data-testid="transfer-confirm-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase B — Client access dialog (Owner-only) */}
      <Dialog open={!!accessFor} onOpenChange={(v) => !v && setAccessFor(null)}>
        <DialogContent className="max-w-lg" data-testid="client-access-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-indigo-600" />
              Client access
            </DialogTitle>
            <DialogDescription>
              {accessFor && (
                <>Choose which of your clients <strong>{accessFor.email}</strong> can see and act on inside this organization.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-zinc-50">
                <input
                  type="radio"
                  className="mt-0.5"
                  checked={accessDraft.mode === 'all'}
                  onChange={() => setAccessDraft({ mode: 'all', ids: [] })}
                  data-testid="access-mode-all"
                />
                <div className="flex-1">
                  <p className="font-medium text-zinc-900">All clients</p>
                  <p className="text-xs text-zinc-500">Member sees every client-tagged post in this org. (Default)</p>
                </div>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-zinc-50">
                <input
                  type="radio"
                  className="mt-0.5"
                  checked={accessDraft.mode === 'scoped'}
                  onChange={() => setAccessDraft((d) => ({ mode: 'scoped', ids: d.ids || [] }))}
                  data-testid="access-mode-scoped"
                />
                <div className="flex-1">
                  <p className="font-medium text-zinc-900">Specific clients only</p>
                  <p className="text-xs text-zinc-500">Pick the clients this member is allowed to see. Other client-tagged posts in this org will be hidden from them.</p>
                </div>
              </label>
            </div>

            {accessDraft.mode === 'scoped' && (
              <div className="border border-zinc-200 rounded p-2 max-h-64 overflow-auto space-y-1">
                {ownerClients.length === 0 ? (
                  <p className="text-xs text-zinc-500 p-3 text-center">
                    You haven't created any clients yet. Add clients first under <Link to="/clients" className="text-indigo-600 underline">Clients</Link>.
                  </p>
                ) : (
                  ownerClients.map((c) => {
                    const checked = accessDraft.ids.includes(c.id);
                    return (
                      <label
                        key={c.id}
                        className="flex items-center gap-2 text-sm p-2 rounded hover:bg-zinc-50 cursor-pointer"
                        data-testid={`access-client-${c.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => setAccessDraft((d) => ({
                            mode: 'scoped',
                            ids: e.target.checked ? [...d.ids, c.id] : d.ids.filter((x) => x !== c.id),
                          }))}
                        />
                        <span
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: c.brand_color || '#6366f1' }}
                        />
                        <span className="flex-1 truncate">{c.name}</span>
                      </label>
                    );
                  })
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessFor(null)} disabled={savingAccess}>
              Cancel
            </Button>
            <Button
              onClick={saveClientAccess}
              disabled={savingAccess}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-client-access-btn"
            >
              {savingAccess && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md" data-testid="delete-org-dialog">
          <DialogHeader>
            <DialogTitle>Delete organization</DialogTitle>
            <DialogDescription>
              This will permanently remove <strong>{org.name}</strong> and detach all org-shared posts (posts will remain owned by their authors). This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
              data-testid="delete-org-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
